import { Helmet } from "react-helmet";
import Wrapper from "../../components/Wrapper";
import { useHistoryLogsFilters } from "../../hooks/useHistoryLogs";
import Select from "react-select";
import { useState } from "react";
import HistoryLogsTable from "./components/HistoryLogsTable/HistoryLogsTable";
import CustomLoader from "../../components/CustomLoader/CustomLoader";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { useTranslation } from "../../hooks/useTranslation";
import './HistoryLogStyles.scss';

// Custom styles for react-select
    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: '#ffffff',
            border: state.isFocused ? '2px solid #4268b0' : '1px solid #e9eaeb',
            borderRadius: '8px',
            boxShadow: state.isFocused ? '0 0 0 1px #4268b0' : 'none',
            '&:hover': {
                border: '1px solid #4268b0'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#4268b0' : state.isFocused ? '#f0f4ff' : '#ffffff',
            color: state.isSelected ? '#ffffff' : '#27323c',
            '&:hover': {
                backgroundColor: state.isSelected ? '#4268b0' : '#f0f4ff'
            }
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e9eaeb'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#718096'
        })
    };

const HistoryLog = () => {
    const {t} = useTranslation();
    const { data: filters, isLoading: filtersLoading, isError: filtersError, error: filtersErrorData } = useHistoryLogsFilters();
    const [equipment, setEquipment] = useState(null);
    const [id, setId] = useState('');
    const [hiddenKeys, setHiddenKeys] = useState(new Set());
    const [showTable, setShowTable] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [submittedEquipment, setSubmittedEquipment] = useState(null);
    const [submittedId, setSubmittedId] = useState('');

    // Handle error toast
    useEffect(() => {
        if (filtersError) {
            toast.error(t('historyLog.failedToLoadFilters'));
        }
    }, [filtersError]);

    const handleEquipmentChange = (e) => {
        setEquipment(e?.value || null);
        // Clear validation error when user selects equipment
        if (e?.value) {
            setValidationErrors(prev => ({ ...prev, equipment: null }));
        }
    }

    const handleIdChange = (e) => {
        const value = e.target.value;
        setId(value);
        // Clear validation error when user enters ID
        if (value) {
            setValidationErrors(prev => ({ ...prev, id: null }));
        }
        // Validate if the value is a number when user types
        if (value && (isNaN(Number(value)) || Number(value) <= 0)) {
            setValidationErrors(prev => ({ ...prev, id: t('historyLog.validation.idMustBePositive') }));
        }
    }

    const validateForm = () => {
        const errors = {};

        if (!equipment) {
            errors.equipment = t('historyLog.validation.selectTable');
        }

        if (!id || id.trim() === '') {
            errors.id = t('historyLog.validation.enterId');
        } else if (isNaN(Number(id)) || Number(id) <= 0) {
            errors.id = t('historyLog.validation.idMustBeNumber');
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleSubmit = () => {
        if (validateForm()) {
            setSubmittedEquipment(equipment);
            setSubmittedId(id);
            setShowTable(true);
        }
    }

    // Toggle key visibility
    const toggleKeyVisibility = (key) => {
        const newHiddenKeys = new Set(hiddenKeys);
        if (newHiddenKeys.has(key)) {
            newHiddenKeys.delete(key);
        } else {
            newHiddenKeys.add(key);
        }
        setHiddenKeys(newHiddenKeys);
    };

    // Show all keys
    const showAllKeys = () => {
        setHiddenKeys(new Set());
    };

    const options = filters?.table?.map(filter => ({
        value: Object.keys(filter)[0],
        label: Object.values(filter)[0]
    })) || [];

    const hasHiddenKeys = hiddenKeys.size > 0;

    return (
        <Wrapper>
            <Helmet>
                <title>{t('historyLog.pageTitle')}</title>
            </Helmet>

            {filtersLoading ? (
                <div className="history-logs__loader">
                    <CustomLoader width={40} />
                    <p>{t('historyLog.loadingFilters')}</p>
                </div>
            ) : filtersError ? (
                <div className="history-logs__error">
                    <div className="history-logs__error-icon">⚠️</div>
                    <h3>{t('historyLog.errorLoadingFilters')}</h3>
                    <p>{filtersErrorData?.message || t('historyLog.failedToLoadFilters')}</p>
                    <button
                        className="history-logs__retry-btn"
                        onClick={() => window.location.reload()}
                    >
                        {t('historyLog.retry')}
                    </button>
                </div>
            ) : (
                <div className="history-logs__container">
                    <div className="history-logs__filters-container">
                        <div className="history-logs__filters">
                            <div className="history-logs__filter-group history-logs__filter-group--small">
                                <label className="history-logs__label">{t('historyLog.tableLabel')}</label>
                                <Select
                                    options={options}
                                    onChange={handleEquipmentChange}
                                    placeholder={t('historyLog.tablePlaceholder')}
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                />
                                {validationErrors.equipment && (
                                    <span className="history-logs__error">{validationErrors.equipment}</span>
                                )}
                            </div>

                            <div className="history-logs__filter-group history-logs__filter-group--small">
                                <label className="history-logs__label">{t('historyLog.idLabel')}</label>
                                <input
                                    type="number"
                                    placeholder={t('historyLog.idPlaceholder')}
                                    onChange={handleIdChange}
                                    className={`history-logs__input ${validationErrors.id ? 'history-logs__input--error' : ''}`}
                                />
                                {validationErrors.id && (
                                    <span className="history-logs__error">{validationErrors.id}</span>
                                )}
                            </div>

                            <div className="history-logs__filter-group history-logs__filter-group--submit">
                                <button
                                    className="history-logs__submit-btn"
                                    onClick={handleSubmit}
                                    disabled={!equipment || !id}
                                >
                                    {t('historyLog.submit')}
                                </button>
                            </div>
                        </div>

                        {/* Hidden Keys Filter */}
                        <div className="history-logs__hidden-keys">
                            <div className="history-logs__hidden-keys-header">
                                <span className="history-logs__hidden-keys-title">{t('historyLog.hiddenFields')}</span>
                            </div>
                                {
                                    hasHiddenKeys ?
                                        <div className="history-logs__hidden-keys-list">
                                            {Array.from(hiddenKeys).map(key => (
                                                <button
                                                    key={key}
                                                    className="history-logs__hidden-key-tag"
                                                    onClick={() => toggleKeyVisibility(key)}
                                                    title={`${t('historyLogTable.actions.showField')} ${key}`}
                                                >
                                                    {key}
                                                    <span className="history-logs__hidden-key-remove">×</span>
                                                </button>
                                            ))}
                                            <button
                                                className="history-logs__show-all-keys"
                                                onClick={showAllKeys}
                                                title={t('historyLog.showAllFields')}
                                            >
                                                {t('historyLog.showAll')}
                                            </button>
                                        </div>
                                    :
                                        <div className="history-logs__hidden-keys-list">
                                            <p>{t('historyLog.noHiddenFields')}</p>
                                        </div>
                                }
                            </div>
                        </div>

                        {/* Placeholder for the table component */}
                            <div className="history-logs__table-container">
                                {
                                    equipment && id ? (
                                        <HistoryLogsTable
                                            equipment={equipment}
                                            id={id}
                                            hiddenKeys={hiddenKeys}
                                            toggleKeyVisibility={toggleKeyVisibility}
                                            showAllKeys={showAllKeys}
                                        />
                                    )
                                    :
                                    <div className="history-logs__table-container empty-state">
                                        <p>{t('historyLog.selectTableAndId')}</p>
                                    </div>
                                }
                        </div>
                </div>
            )}
        </Wrapper>
    );
};

export default HistoryLog;
